/**
 * The shoulder recenter's declaration in the file (`extras.poqpoq.shoulderRecenter`
 * on the Upperarm node) — the 'cap-height' method's twin of elbowPlacement.
 *
 * WHY THE FILE MUST SAY IT (ADR-030 §14, the fox-onesie law): 'cap-height'
 * is a measurement re-derived on load. A reopened export whose pivot was
 * already raised would measure itself near the band and propose ~nothing —
 * but "near the band" cannot tell a raised T-body from a born A-body, and a
 * human's authored placement would be re-measured away. The marker is the
 * file saying WHO decided and HOW MUCH, so a Document replaying 'cap-height'
 * on a file that already carries it ADOPTS instead of raising twice.
 *
 * `totalOffset` is in the joint's bind frame, in world distance units (the
 * elbow marker's convention); `fractionBefore` is the measured seat that
 * justified the raise — the evidence, kept beside the answer.
 *
 * Depends on: poqpoq-extras, zod. Depended on by: run-document (the door),
 * the adapter's scene twin, package-census (generic key listing).
 */
import { z } from 'zod';
import { readPoqpoqExtra, withPoqpoqExtra } from './poqpoq-extras.js';

export const SHOULDER_RECENTER_KEY = 'shoulderRecenter';

const markerSchema = z.object({
  version: z.literal(1),
  method: z.literal('cap-height'),
  /** 'measured' = the instrument placed it; 'authored' = a person's offset won. */
  provenance: z.enum(['measured', 'authored']),
  source: z.string().min(1),
  fractionBefore: z.number().finite(),
  targetFraction: z.number().finite(),
  totalOffset: z.object({ x: z.number().finite(), y: z.number().finite(), z: z.number().finite() }),
});
export type ShoulderRecenterMarker = z.infer<typeof markerSchema>;

export function parseShoulderRecenterMarker(data: unknown, where: string): ShoulderRecenterMarker | null {
  const raw = readPoqpoqExtra(data, SHOULDER_RECENTER_KEY);
  if (raw === undefined) return null;
  const parsed = markerSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`${where}: malformed extras.poqpoq.${SHOULDER_RECENTER_KEY}: ${parsed.error.message}`);
  }
  return parsed.data;
}

export function withShoulderRecenterMarker(data: unknown, marker: ShoulderRecenterMarker): Record<string, unknown> {
  return withPoqpoqExtra(data, SHOULDER_RECENTER_KEY, markerSchema.parse(marker));
}
